import { Component, HostListener, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { AbstractComponent, ChatChannelTypeEnum, ChatConversation, ChatUserDisplayMap, ColorThemeService } from 'lib-trend-core';
import {
  ChatActions,
  ChatAppTarget,
  ChatBadgeService,
  ChatConversationDialogsService,
  ChatConversationListMessageHit,
  ChatConversationListPane,
  ChatDialogService,
  ChatPresenceView,
  ChatPushService,
  ChatShellService,
  activeChannelIdSelector,
  activeConversationSelector,
  bootstrapFailedSelector,
  chatUnavailableSelector,
  buildChatAppPath,
  directDraftSelector,
  parseChatAppPath,
  presenceSelector,
  resolveChatInitials,
  resolvePresenceView,
  statusesSelector,
  toggleChatStatusDialog,
  totalUnreadSelector,
  userDisplaySelector,
} from 'lib-trend-core/chat';
import { combineLatest, fromEvent } from 'rxjs';
import { distinctUntilChanged, filter, map, skip, startWith, takeUntil } from 'rxjs/operators';
import { ChatAppActivationService } from '../core/chat-app-activation.service';
import { ChatAppBadgeService } from '../core/chat-app-badge.service';
import { ChatAppInstallMode, ChatAppInstallService } from '../core/chat-app-install.service';
import { ChatAppOpenerService } from '../core/chat-app-opener.service';
import { ChatAppSessionService } from '../core/chat-app-session.service';
import { ChatAppUpdateService } from '../core/chat-app-update.service';
import { ChatAppInstallStepsComponent } from './chat-app-install-steps.component';

/** The views the rail lists as shortcuts (the search pane has none). */
const RAIL_PANES: ReadonlyArray<ChatConversationListPane> = ['threads', 'saved', 'team'];

/**
 * The chat app's shell — SPEC-2026-09-internal-chat-pwa (T-30 · T-31 · T-35 · T-36 · T-37).
 *
 * Left: the same conversation list the product's bubble renders, as a rail. Right: the
 * routed pane. Under the phone breakpoint the route decides which of the two is on screen
 * (list → conversation → thread, each with a way back).
 *
 * The address is the source of truth for what is open (RF-02). Anything that opens a
 * conversation without going through the router — a create dialog, the quick switcher, a
 * notification, a person's card — reaches the store or the shell bus first; this shell
 * turns that into a navigation, and the routes turn navigations back into store state.
 * Each direction only acts on a real difference, so the two never loop.
 */
@Component({
  selector: 'chat-app-shell',
  templateUrl: './chat-app-shell.component.html',
  styleUrls: ['./chat-app-shell.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatAppShellComponent extends AbstractComponent implements OnInit {

  activeChannelId: string | null = null;

  /** A pane other than the list is on screen — drives the phone master/detail. */
  paneOpen = false;

  /** The rail pane on screen, for its shortcut's highlight; null on a conversation. */
  activePane: ChatConversationListPane | null = null;

  myPresence: ChatPresenceView = resolvePresenceView(undefined, true);

  installMode: ChatAppInstallMode = 'none';

  notificationPermission: NotificationPermission | 'unsupported' = 'unsupported';

  readonly chatUnavailable$ = this.store.select(chatUnavailableSelector);

  readonly bootstrapFailed$ = this.store.select(bootstrapFailedSelector);

  constructor(
    injector: Injector,
    private readonly store: Store,
    private readonly titleService: Title,
    private readonly session: ChatAppSessionService,
    private readonly chatBadge: ChatBadgeService,
    private readonly chatShell: ChatShellService,
    private readonly chatPush: ChatPushService,
    private readonly dialog: ChatDialogService,
    private readonly conversationDialogs: ChatConversationDialogsService,
    private readonly appBadge: ChatAppBadgeService,
    private readonly installService: ChatAppInstallService,
    readonly updateService: ChatAppUpdateService,
    private readonly opener: ChatAppOpenerService,
    private readonly activation: ChatAppActivationService,
    private readonly colorTheme: ColorThemeService,
  ) {
    super(injector);
  }

  get currentUser() {
    return this.session.user;
  }

  get productUrl(): string {
    return String(this.environment['trendWebAppUrl'] ?? '');
  }

  /** Explicit call-to-action while the browser still asks (T-35). */
  get showNotificationCta(): boolean {
    return this.notificationPermission === 'default';
  }

  /** Install card: installable, not installed, not dismissed on this device (T-36). */
  get showInstallCard(): boolean {
    return this.installMode !== 'none' && !this.installService.dismissed;
  }


  get isDarkTheme(): boolean {
    return this.colorTheme.current === 'dark';
  }

  ngOnInit(): void {
    this.session.start();
    this.chatBadge.init();
    this.appBadge.start();
    this.updateService.start();
    this.opener.start();
    this.installService.start();
    this.notificationPermission = this.chatPush.permissionState;

    this.installService.mode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => this.installMode = mode);

    this.trackRoute();
    this.bridgeStoreToAddress();
    this.bridgeShellTargets();
    this.trackTitle();
    this.trackMyPresence();
    this.trackNotificationActivations();

    this.session.sessionClearedElsewhere$()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => window.location.assign('/login'));
  }

  // ---------------------------------------------------------------------------
  // Rail actions
  // ---------------------------------------------------------------------------

  openConversation(channelId: string): void {
    this.navigate({ channelId });
  }

  openMessage(hit: ChatConversationListMessageHit): void {
    this.navigate({ channelId: hit.channelId, messageId: hit.messageId });
  }

  openPane(pane: ChatConversationListPane): void {
    this.navigate({ view: pane });
  }

  openStatusPicker(): void {
    toggleChatStatusDialog(this.dialog);
  }

  openPreferences(): void {
    this.conversationDialogs.openPreferences();
  }

  toggleTheme(): void {
    this.colorTheme.toggle();
  }

  async enableNotifications(): Promise<void> {
    await this.chatPush.enableNotifications();
    this.notificationPermission = this.chatPush.permissionState;
  }

  dismissNotificationCta(): void {
    // Not a permission decision: the browser keeps asking the next time the app loads.
    this.notificationPermission = 'unsupported';
  }

  async install(): Promise<void> {
    if (this.installMode === 'prompt') {
      await this.installService.promptInstall();
      return;
    }
    this.dialog.open(ChatAppInstallStepsComponent, { width: '480px', data: this.installMode });
  }

  dismissInstall(): void {
    this.installService.dismiss();
    this.installMode = 'none';
  }

  retryBootstrap(): void {
    this.chatBadge.refresh();
  }

  signOut(): void {
    this.session.end('signedOut', null);
  }

  resolveInitials(name: string | undefined): string {
    return resolveChatInitials(name ?? '');
  }

  /** Quick switcher (Ctrl/Cmd+K) is registered by ChatStateModule; `/` focuses search on desktop. */
  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || event.defaultPrevented || event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
    const searchInput = document.querySelector<HTMLInputElement>('.chat-app__rail input[type="search"]');
    // Hidden with the collapsed rail: let the keystroke through instead of eating it.
    if (searchInput?.offsetParent) {
      event.preventDefault();
      searchInput.focus();
    }
  }

  // ---------------------------------------------------------------------------
  // Address ↔ state
  // ---------------------------------------------------------------------------

  private navigate(target: ChatAppTarget, replaceUrl = false): void {
    this.router.navigateByUrl(buildChatAppPath(target), { replaceUrl });
  }

  private currentTarget(): ChatAppTarget {
    return parseChatAppPath(this.router.url) ?? {};
  }

  private trackRoute(): void {
    const update = () => {
      const target = this.currentTarget();
      this.paneOpen = !!(target.channelId || target.directUserId || target.view);
      this.activePane = RAIL_PANES.includes(target.view as ChatConversationListPane)
        ? (target.view as ChatConversationListPane)
        : null;
    };
    update();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), takeUntil(this.destroy$))
      .subscribe(update);
  }

  /** Store → address: a conversation or draft opened by anything but the router. */
  private bridgeStoreToAddress(): void {
    this.store.select(activeChannelIdSelector)
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((channelId) => this.activeChannelId = channelId ?? null);

    this.store.select(activeChannelIdSelector)
      .pipe(distinctUntilChanged(), skip(1), filter((channelId): channelId is string => !!channelId), takeUntil(this.destroy$))
      .subscribe((channelId) => {
        const current = this.currentTarget();
        if (current.channelId === channelId) return;
        // A draft that just became a real conversation replaces its own address.
        this.navigate({ channelId }, !!current.directUserId);
      });

    this.store.select(directDraftSelector)
      .pipe(
        map((draft) => draft?.userId ?? null),
        distinctUntilChanged(),
        skip(1),
        filter((userId): userId is string => !!userId),
        takeUntil(this.destroy$),
      )
      .subscribe((directUserId) => {
        if (this.currentTarget().directUserId === directUserId) return;
        this.navigate({ directUserId });
      });

    this.conversationDialogs.created$
      .pipe(takeUntil(this.destroy$))
      .subscribe((created) => this.navigate(created.channelId ? { channelId: created.channelId } : { directUserId: created.directUserId }));
  }

  /** Shell bus → address: the quick switcher, the status dialog, push clicks. */
  private bridgeShellTargets(): void {
    this.chatShell.targets$
      .pipe(takeUntil(this.destroy$))
      .subscribe((target) => this.navigate({
        channelId: target.channelId,
        messageId: target.messageId,
        threadRootId: target.threadRootId,
        directUserId: target.directUserId,
        view: target.view,
        searchTerm: target.searchTerm,
      }));
  }

  private trackTitle(): void {
    combineLatest([
      this.store.select(activeConversationSelector),
      this.store.select(userDisplaySelector),
      this.store.select(totalUnreadSelector),
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd), startWith(null)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([conversation, userDisplay, total]) => this.applyTitle(conversation, userDisplay, total));
  }

  private applyTitle(conversation: ChatConversation | null, userDisplay: ChatUserDisplayMap, total: number): void {
    const appName = 'Trendmulti Chat';
    const name = this.paneOpen && this.currentTarget().channelId ? this.conversationName(conversation, userDisplay) : '';
    const unread = total > 0 ? `(${total > 99 ? '99+' : total}) ` : '';
    this.titleService.setTitle(`${unread}${name ? `${name} · ` : ''}${appName}`);
  }

  private conversationName(conversation: ChatConversation | null, userDisplay: ChatUserDisplayMap): string {
    if (!conversation) return '';
    if (conversation.type === ChatChannelTypeEnum.DIRECT) {
      const me = this.getIDCurrentUser();
      const counterpart = (conversation.members ?? []).find((id) => id !== me) ?? me;
      return userDisplay?.[counterpart]?.name ?? '';
    }
    return conversation.name ?? '';
  }

  private trackMyPresence(): void {
    combineLatest([this.store.select(statusesSelector), this.store.select(presenceSelector)])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([statuses, presence]) => {
        const me = this.getIDCurrentUser();
        this.myPresence = resolvePresenceView(statuses[me], presence[me] ?? true);
      });
  }

  /**
   * A notification activated with the app closed lands on `?from=notification`; with the
   * app open, the worker posts the conversation. Either way the route reports whether the
   * intended conversation opened (T-39).
   */
  private trackNotificationActivations(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.get('from') === 'notification') {
      this.activation.expect(this.currentTarget().channelId ?? null);
      url.searchParams.delete('from');
      this.router.navigateByUrl(`${url.pathname}${url.search}`, { replaceUrl: true });
    }

    if ('serviceWorker' in navigator) {
      fromEvent<MessageEvent>(navigator.serviceWorker, 'message')
        .pipe(
          filter((event) => event.data?.type === 'CHAT_PUSH_NAVIGATE' && event.data?.fromNotification === true),
          takeUntil(this.destroy$),
        )
        .subscribe((event) => {
          const channelId: string = event.data.channelId ?? '';
          this.activation.expect(channelId || null);
          this.navigate(channelId ? { channelId, messageId: event.data.messageId || undefined } : {});
        });
    }

    // The "Salvas (n)" counter of the rail needs the bookmark list.
    this.store.dispatch(ChatActions.loadSaved());
  }
}
